import { FetchNextPageOptions, useInfiniteQuery } from '@tanstack/react-query'
import { flatten, isNil } from 'ramda'

import { Observation, ObservationsResult } from '../../types/observation'
import { get } from '../../utils/api'
import { getOffsetFromUrl, getQueryString } from '../../utils/queryString'

export const OBSERVATIONS_PARAMS_NO_IMAGE_DATA = 'no_image_data'
export const OBSERVATIONS_PARAMS_DEFAULT_LIMIT = 65535

export type ObservationsParamsAll = {
  productId?: number
  trainingId?: number
  trainingTestId?: number
  availableForTesting?: number
  annotationClasses?: string
  productClasses?: string[]
  mode?: typeof OBSERVATIONS_PARAMS_NO_IMAGE_DATA
  limit?: number
  offset?: number
  dataset?: string[]
  datasetExclude?: boolean
  filename?: string
}

export const useObservationsAll = (params: ObservationsParamsAll) => {
  const response = useInfiniteQuery(
    ['observationsAll', params],
    ({ pageParam }) => {
      const queryString = getQueryString({
        offset: pageParam,
        ...getObservationsQueryParams(params),
      })
      return get<ObservationsResult>(`/observations/?${queryString}`)
    },
    {
      getNextPageParam: (lastPage) => getOffsetFromUrl(lastPage.next),
      retry: false,
      keepPreviousData: true,
    }
  )

  const dataAll = mergePages(response.data?.pages)

  const fetchAll = (options?: FetchNextPageOptions | undefined) =>
    !response.isFetching && response.fetchNextPage(options)

  return {
    ...response,
    dataAll,
    fetchAll,
  }
}

const isObservationResult = (
  result: ObservationsResult | undefined
): result is ObservationsResult => !isNil(result)

/** Merge infinite pages results */
const mergePages = (
  pages: ObservationsResult[] | undefined
): Observation[] | undefined => {
  if (!pages) return undefined
  return flatten(pages.filter(isObservationResult).map((page) => page.results))
}

/** Transform given params in params that /observations endpoint can understand */
const getObservationsQueryParams = (params: ObservationsParamsAll) => ({
  product: params.productId,
  training: params.trainingId,
  trainingTest: params.trainingTestId,
  availableForTesting: params.availableForTesting,
  annotationClasses: params.annotationClasses,
  productClasses:
    params.productClasses && params.productClasses.length
      ? params.productClasses.join()
      : undefined,
  mode: OBSERVATIONS_PARAMS_NO_IMAGE_DATA,
  limit: OBSERVATIONS_PARAMS_DEFAULT_LIMIT,
  dataset:
    params.dataset && params.dataset.length ? params.dataset.join() : undefined,
  dataset_exclude: params.datasetExclude,
  filename: params.filename,
})
